/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type {
  PrenigmaCollaborationEnvelope,
  PrenigmaCollaborationRuntimeSourceProducer,
} from "./collaborationRuntime";
import {
  cancelPrenigmaTtsMediaPlayback,
  type PrenigmaTtsControllableMediaElement,
  type PrenigmaTtsMediaControlRefs,
} from "./voiceControlMediaSession";

export interface ActivePrenigmaTtsPlayback {
  correlationIds?: string[];
  media: PrenigmaTtsControllableMediaElement;
  refs: Omit<PrenigmaTtsMediaControlRefs, "supersedesTurnId"> & {
    supersedesTurnId?: string;
  };
}

export interface CancelActivePrenigmaTtsPlaybackOptions {
  correlationIds?: string[];
  supersedesTurnId: string;
}

export interface CancelActivePrenigmaTtsPlaybackResult {
  activeCleared: boolean;
  bargeIn?: PrenigmaCollaborationEnvelope;
  cancelVerified: boolean;
  currentTimeResetAfterCancel: boolean;
  obsoleteAudioSuppressed: boolean;
  pausedAfterCancel: boolean;
  status: "cancelled" | "not_active";
  ttsCancel?: PrenigmaCollaborationEnvelope;
  ttsCancelAcknowledged: boolean;
}

export interface PrenigmaTtsPlaybackController {
  cancelActivePlayback: (
    options: CancelActivePrenigmaTtsPlaybackOptions,
  ) => CancelActivePrenigmaTtsPlaybackResult;
  clearActivePlayback: (obsoleteTurnId?: string) => boolean;
  getActiveTurnId: () => string | undefined;
  setActivePlayback: (playback: ActivePrenigmaTtsPlayback) => void;
}

export type PrenigmaTtsPlaybackRegistrationRefs = Omit<
  PrenigmaTtsMediaControlRefs,
  "supersedesTurnId"
>;

export interface RegisterPrenigmaTtsMediaPlaybackOptions {
  controller: PrenigmaTtsPlaybackController | undefined;
  correlationIds?: string[];
  media: PrenigmaTtsControllableMediaElement;
  obsoleteTurnId: string;
  refNamespace?: string;
  refs?: Partial<PrenigmaTtsPlaybackRegistrationRefs>;
}

export interface RegisterPrenigmaTtsMediaPlaybackResult {
  activeTurnId?: string;
  refs?: PrenigmaTtsPlaybackRegistrationRefs;
  registered: boolean;
  status: "controller_unavailable" | "registered";
}

export function registerPrenigmaTtsMediaPlayback({
  controller,
  correlationIds = [],
  media,
  obsoleteTurnId,
  refNamespace = "element-call",
  refs = {},
}: RegisterPrenigmaTtsMediaPlaybackOptions):
  RegisterPrenigmaTtsMediaPlaybackResult {
  if (controller === undefined) {
    return {
      registered: false,
      status: "controller_unavailable",
    };
  }

  const generatedRefs = buildPrenigmaTtsPlaybackRefs({
    obsoleteTurnId,
    refNamespace,
  });
  const resolvedRefs: PrenigmaTtsPlaybackRegistrationRefs = {
    obsoleteTurnId: textOrDefault(refs.obsoleteTurnId, generatedRefs.obsoleteTurnId),
    obsoleteTtsStreamRef: textOrDefault(
      refs.obsoleteTtsStreamRef,
      generatedRefs.obsoleteTtsStreamRef,
    ),
    bargeInSourceEventRef: textOrDefault(
      refs.bargeInSourceEventRef,
      generatedRefs.bargeInSourceEventRef,
    ),
    bargeInTimelineReadbackRef: textOrDefault(
      refs.bargeInTimelineReadbackRef,
      generatedRefs.bargeInTimelineReadbackRef,
    ),
    ttsCancelAckRef: textOrDefault(
      refs.ttsCancelAckRef,
      generatedRefs.ttsCancelAckRef,
    ),
  };

  requirePlaybackRegistrationRefs(resolvedRefs);
  controller.setActivePlayback({
    correlationIds,
    media,
    refs: resolvedRefs,
  });
  return {
    activeTurnId: controller.getActiveTurnId(),
    refs: resolvedRefs,
    registered: true,
    status: "registered",
  };
}

export function createPrenigmaTtsPlaybackController(
  producer: PrenigmaCollaborationRuntimeSourceProducer | undefined,
): PrenigmaTtsPlaybackController | undefined {
  if (producer === undefined) return undefined;

  let activePlayback: ActivePrenigmaTtsPlayback | undefined;

  return {
    cancelActivePlayback({
      correlationIds = [],
      supersedesTurnId,
    }: CancelActivePrenigmaTtsPlaybackOptions): CancelActivePrenigmaTtsPlaybackResult {
      requireText(supersedesTurnId, "supersedesTurnId");
      if (activePlayback === undefined) {
        return emptyCancelResult("not_active");
      }

      const refs: PrenigmaTtsMediaControlRefs = {
        ...activePlayback.refs,
        supersedesTurnId:
          activePlayback.refs.supersedesTurnId ?? supersedesTurnId,
      };
      const result = cancelPrenigmaTtsMediaPlayback({
        correlationIds: [
          ...(activePlayback.correlationIds ?? []),
          ...correlationIds,
        ],
        media: activePlayback.media,
        producer,
        refs,
      });
      activePlayback = undefined;
      return {
        activeCleared: true,
        bargeIn: result.bargeIn,
        cancelVerified: result.cancelVerified,
        currentTimeResetAfterCancel: result.currentTimeResetAfterCancel,
        obsoleteAudioSuppressed: result.obsoleteAudioSuppressed,
        pausedAfterCancel: result.pausedAfterCancel,
        status: "cancelled",
        ttsCancel: result.ttsCancel,
        ttsCancelAcknowledged: result.ttsCancelAcknowledged,
      };
    },
    clearActivePlayback(obsoleteTurnId?: string): boolean {
      if (activePlayback === undefined) return false;
      if (
        obsoleteTurnId !== undefined &&
        activePlayback.refs.obsoleteTurnId !== obsoleteTurnId
      ) {
        return false;
      }
      activePlayback = undefined;
      return true;
    },
    getActiveTurnId(): string | undefined {
      return activePlayback?.refs.obsoleteTurnId;
    },
    setActivePlayback(playback: ActivePrenigmaTtsPlayback): void {
      requireText(playback.refs.obsoleteTurnId, "refs.obsoleteTurnId");
      requireText(
        playback.refs.obsoleteTtsStreamRef,
        "refs.obsoleteTtsStreamRef",
      );
      requireText(
        playback.refs.bargeInSourceEventRef,
        "refs.bargeInSourceEventRef",
      );
      requireText(
        playback.refs.bargeInTimelineReadbackRef,
        "refs.bargeInTimelineReadbackRef",
      );
      requireText(playback.refs.ttsCancelAckRef, "refs.ttsCancelAckRef");
      activePlayback = playback;
    },
  };
}

function emptyCancelResult(
  status: "not_active",
): CancelActivePrenigmaTtsPlaybackResult {
  return {
    activeCleared: false,
    cancelVerified: false,
    currentTimeResetAfterCancel: false,
    obsoleteAudioSuppressed: false,
    pausedAfterCancel: false,
    status,
    ttsCancelAcknowledged: false,
  };
}

function requireText(value: string, path: string): void {
  if (!value.trim()) {
    throw new Error(`${path} is required`);
  }
}

function buildPrenigmaTtsPlaybackRefs({
  obsoleteTurnId,
  refNamespace,
}: {
  obsoleteTurnId: string;
  refNamespace: string;
}): PrenigmaTtsPlaybackRegistrationRefs {
  requireText(obsoleteTurnId, "obsoleteTurnId");
  const safeNamespace = safeRefSegment(refNamespace, "refNamespace");
  const safeTurnId = safeRefSegment(obsoleteTurnId, "obsoleteTurnId");
  return {
    obsoleteTurnId,
    obsoleteTtsStreamRef: `tts_stream_ref://${safeNamespace}/${safeTurnId}/audio`,
    bargeInSourceEventRef:
      `source_event_ref://${safeNamespace}/${safeTurnId}/barge-in`,
    bargeInTimelineReadbackRef:
      `matrix_timeline_ref://${safeNamespace}/${safeTurnId}/barge-in`,
    ttsCancelAckRef:
      `tts_cancel_ack_ref://${safeNamespace}/${safeTurnId}/cancelled`,
  };
}

function requirePlaybackRegistrationRefs(
  refs: PrenigmaTtsPlaybackRegistrationRefs,
): void {
  requireText(refs.obsoleteTurnId, "refs.obsoleteTurnId");
  requirePrefixedRef(
    refs.obsoleteTtsStreamRef,
    "tts_stream_ref://",
    "refs.obsoleteTtsStreamRef",
  );
  requirePrefixedRef(
    refs.bargeInSourceEventRef,
    "source_event_ref://",
    "refs.bargeInSourceEventRef",
  );
  requirePrefixedRef(
    refs.bargeInTimelineReadbackRef,
    "matrix_timeline_ref://",
    "refs.bargeInTimelineReadbackRef",
  );
  requirePrefixedRef(
    refs.ttsCancelAckRef,
    "tts_cancel_ack_ref://",
    "refs.ttsCancelAckRef",
  );
}

function requirePrefixedRef(value: string, prefix: string, path: string): void {
  requireText(value, path);
  if (!value.startsWith(prefix)) {
    throw new Error(`${path} must start with ${prefix}`);
  }
}

function safeRefSegment(value: string, path: string): string {
  const normalized = value.replace(/[^a-zA-Z0-9._:-]/g, "_").slice(0, 180);
  requireText(normalized, path);
  return normalized;
}

function textOrDefault(value: string | undefined, defaultValue: string): string {
  const text = String(value ?? "").trim();
  return text || defaultValue;
}
